// Copyright (c) 2020-2024 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { Checkbox, FormControlLabel, Grid, Tooltip } from '@mui/material';
import { withStyles } from "tss-react/mui";
import FormController from 'mobx-react-form';
import { LocationDescriptorObject, UnregisterCallback } from 'history';
import { observer } from 'mobx-react';
import { action, computed, makeObservable, observable, toJS } from 'mobx';
import isEqual from 'lodash/isEqual';
import cx from 'classnames';

import Button, { ButtonProps } from '@shared/components/Button';
import Loading from '@shared/components/Loading';
import FormHeader, { FormHeaderProps } from './components/FormHeader';
import FormContainer from './components/FormContainer';
import FormFooter from './components/FormFooter';
import FormContent from './components/FormContent';
import FooterButtonsWrapper from './components/FooterButtonsWrapper';
import { showNotification, NotificationType } from '@shared/components/Notification';
import history from '@shared/utils/history';
import { errObj2Str, getTestAttr } from '@shared/utils/common';
import { getInvalidFields, scrollToInvalidField, resetForm } from '@shared/utils/form';
import container from '@core/di';
import AuthStore from '@core/stores/auth';

export enum ManageMode {
  create = 'create',
  edit = 'edit'
}

const LOCAL_STORAGE_KEYS = {
  withCreateAnother: 'withCreateAnother'
};

export type Data = { [key: string]: any };

// Prop interface for the main class.
export interface FormProps {
  classes?: any; // to suppress linting warnings.
  className?: string;
  moduleName: string;
  manageMode: ManageMode;
  closeLocation: LocationDescriptorObject;
  form: FormController;
  storedData: Data;
  formElementProps?: { [key: string]: any };
  heading?: FormHeaderProps['heading'];
  closeOnSubmit?: boolean;
  loadingData?: boolean;
  successMessage: string;
  createBtnProps?: ButtonProps;
  cancelBtnProps?: ButtonProps;
  saveBtnProps?: ButtonProps;
  ignoringFieldsForConfirmation?: Array<string>;
  withFooter?: boolean;
  withHeader?: boolean;
  withTemplate?: boolean;
  withScrollingToError?: boolean;
  withLeaveConfirmation?: boolean;
  withCancelButtonOnCreate?: boolean;
  withCreateAnother?: boolean;
  viewOnly?: boolean;
  viewOnlyMessage?: string;
  createAnotherLabel?: string;
  closeComponent?: FormHeaderProps['closeComponent'];
  controlsComponent?: JSX.Element | null;
  updateStoredData: (data: Data) => any;
  onSubmit: (data: Data, withCreateAnother?: boolean) => Promise<any> | any;
  onSetTemplateClick?: () => any;
  onErrorSubmit?: (form, responseErr?) => any;
  onCancelClick?: () => any;
  onUnmount?: () => any;
  shallNextLocationTerminateFormInstance?: (currentLocation: LocationDescriptorObject, nextLocation: LocationDescriptorObject) => boolean;
  imperativeValidate?: () => boolean;
  children: React.ReactNode;
}

const defaultShallNextLocationTerminateFormInstance = (
  currentLocation: LocationDescriptorObject,
  nextLocation: LocationDescriptorObject
) => {
  return currentLocation.pathname !== nextLocation.pathname;
};

@observer
class Form extends React.Component<FormProps> {
  static defaultProps = {
    withFooter: true,
    withHeader: true,
    withScrollingToError: true,
    withLeaveConfirmation: true,
    withCreateAnother: true,
    createAnotherLabel: 'Create another',
    ignoringFieldsForConfirmation: [],
  };
  private authStore = container.get<AuthStore>(AuthStore.diToken);  // for checking logged in.
  @observable useCreateAnother = Boolean(localStorage.getItem(LOCAL_STORAGE_KEYS.withCreateAnother));
  @observable _submitting = false;
  @observable isOpenSetTemplateTooltip = false;

  constructor(props: FormProps) {
    super(props);
    makeObservable(this);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private unblockLocation: UnregisterCallback | undefined;  // callback to stop warning when leaving the form with unsaved data.

  // Compare new and current form data.
  private get hasUnsavedChanges() {
    const { form, storedData, ignoringFieldsForConfirmation } = this.props;
    const currentData = form.values();

    ignoringFieldsForConfirmation?.forEach((field) => {
      delete storedData[field];
      delete currentData[field];
    });

    return !isEqual(currentData, toJS(storedData));
  }

  // Get msg to show when user leaving page with unsaved data.
  private getConfirmationLabel = (nextLocation: LocationDescriptorObject): string | undefined => {
    const { shallNextLocationTerminateFormInstance = defaultShallNextLocationTerminateFormInstance } = this.props;

    const shouldShow = (
      shallNextLocationTerminateFormInstance(history.location, nextLocation) &&
      this.hasUnsavedChanges &&
      this.authStore.loggedIn
    );

    if (shouldShow) {
      return 'Are you sure you want to leave? All changes will be lost.';
    }
  }

  // Fired after initial loading of the component.
  componentDidMount() {
    const { withLeaveConfirmation } = this.props;

    if (withLeaveConfirmation) {
      // history.block() returns a callback to stop blocking navigating.
      this.unblockLocation = history.block((prompt) => this.getConfirmationLabel(prompt));  // history.block prevents navigating away from the current page
    }
  }

  // Fired before unload the component.
  componentWillUnmount() {
    //console.log("**** HERE in basic form compnentwillunmount")
    if(this.unblockLocation) this.unblockLocation();  // stop blocking navigating.

    const { form, onUnmount } = this.props;

    resetForm(form);

    if (onUnmount) {
      onUnmount();
    }
  }

  // Set up the buttons for the form.
  private get controls() {
    // console.log(this.props);
    const { controlsComponent } = this.props;

    // Use user define (?)
    if (controlsComponent) {
      return controlsComponent;
    }

    // Build buttons from data in props.
    const {
      createBtnProps,
      saveBtnProps,
      cancelBtnProps,
      moduleName,
      createAnotherLabel,
      withTemplate,
      withCancelButtonOnCreate,
      withCreateAnother,
      viewOnly,
      viewOnlyMessage = 'You cannot manage this form',
    } = this.props;
    const classes = withStyles.getClasses(this.props);

    // Create array with two sets of buttons 'create/cancel/another' and 'save/cancel/template'.
    const controls = {
      [ManageMode.create]: (
        <FooterButtonsWrapper message={viewOnlyMessage} disabled={viewOnly} key={ManageMode.create}>
          <Button
            {...getTestAttr(moduleName, `${ManageMode.create}-create`, 'btn')}
            text="Create"
            type="submit"
            loading={this.submitting}
            {...createBtnProps}
          />
          {withCancelButtonOnCreate && (
            <Button
              {...getTestAttr(moduleName, `${ManageMode.create}-cancel`, 'btn')}
              text="Cancel"
              color="primary"
              onClick={this.handleCancel}
              {...cancelBtnProps}
            />
          )}
          {withCreateAnother && (
            <FormControlLabel
              {...getTestAttr(moduleName, `${ManageMode.create}-another`, 'label')}
              control={<Checkbox />}
              label={createAnotherLabel}
              classes={{
                label: classes.createAnotherLabel
              }}
              checked={this.useCreateAnother}
              onChange={this.handleCreateAnotherChange}
            />
          )}
        </FooterButtonsWrapper>
      ),
      [ManageMode.edit]: (
        <React.Fragment key={ManageMode.edit}>
          <FooterButtonsWrapper message={viewOnlyMessage} disabled={viewOnly}>
            <Button
              {...getTestAttr(moduleName, `${ManageMode.edit}-save`, 'btn')}
              text="Save"
              type="submit"
              loading={this.submitting}
              {...saveBtnProps}
            />
            <Button
              {...getTestAttr(moduleName, `${ManageMode.edit}-cancel`, 'btn')}
              text="Cancel"
              color="primary"
              onClick={this.handleCancel}
              {...cancelBtnProps}
            />
          </FooterButtonsWrapper>
          {withTemplate && (
            <Tooltip open={this.isOpenSetTemplateTooltip} title="Successfully set template">
              <span className={classes.templateButton} onClick={this.handleSetTemplateClick}>Set as template</span>
            </Tooltip>
          )}
        </React.Fragment>
      ),
    };

    // return one of the two set.
    return controls[this.props.manageMode];
  }

  // Navigate to a given URL on close (?).
  private close = () => {
    //console.log("**** HERE in basic form close")
    const { closeLocation } = this.props;

    history.push(closeLocation);
  }

  // Invoke a callback to set the current dataset as a template.
  @action handleSetTemplateClick = () => {
    const { onSetTemplateClick } = this.props;

    if (onSetTemplateClick) {
      try {
        onSetTemplateClick();

        this.isOpenSetTemplateTooltip = true;

        const showTime = 2000;

        setTimeout(() => {
          this.isOpenSetTemplateTooltip = false;
        }, showTime);
      } catch {
        showNotification('Failed to set template', NotificationType.error);
      }
    }
  }

  // set/clear submitting flag.
  @action setSubmitting(submitting: boolean) {
    this._submitting = submitting;
  }

  @computed get submitting() {
    return this._submitting;
  }

  // set/clear the 'Create Another' checkbox (?).
  @action private handleCreateAnotherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.persist();

    const { checked } = e.target;

    this.useCreateAnother = checked;

    if (checked) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.withCreateAnother, checked.toString());
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.withCreateAnother);
    }
  }

  // Invoke cancel callback and close the form (ie. navigate to some url).
  private handleCancel = () => {
    const { onCancelClick } = this.props;

    if (onCancelClick) {
      onCancelClick();
    }

    this.close();
  }

  // Invoke submit error callback and optionally scroll to the invalid field.
  private handleErrorOnSubmit = () => {
    const { withScrollingToError, form, onErrorSubmit } = this.props;
    console.log("****** In errorOnSubmit.")

    // Invoke callback provided by the calling component.
    if (onErrorSubmit) {
      onErrorSubmit(form);
    }

    if (withScrollingToError) {
      const invalidFields = getInvalidFields(form);
      console.log("****** Invalid fields:", invalidFields);

      if (invalidFields.length) {
        scrollToInvalidField(invalidFields[0]);
      }
    }
  }

  // Check form input, invoke submit callback, 
  @action async handleSubmit(e) {
    e.preventDefault();
    //console.log("***** HERE in basic form handleSubmit")

    const { form, imperativeValidate } = this.props;

    form.validate();

    const isValid = imperativeValidate ? imperativeValidate() : form.isValid;

    if (!isValid) {
      this.handleErrorOnSubmit();
      form.showErrors();

      return;
    }
    
    // set submitting flag.
    this.setSubmitting(true);

    // Stop blocking to avoid showing the "unsaved changes" confirmation message.
    if (this.unblockLocation) {
      this.unblockLocation();
      this.unblockLocation = undefined; // Clear the reference
    }

    try {
      const data = form.values();

      // Invoke callback.
      await this.props.onSubmit(data, this.useCreateAnother);

      const { manageMode, successMessage, closeOnSubmit, withTemplate } = this.props;

      showNotification(successMessage);

      // clear submitting flag.
      this.setSubmitting(false);

      // Update current form data.
      if (manageMode === ManageMode.create) {
        if (!withTemplate) {
          resetForm(form);

          this.props.updateStoredData(form.values());
        }

        if (this.useCreateAnother) {
          return;
        }

        this.props.updateStoredData(form.values());
        this.close();

        return;
      }

      this.props.updateStoredData(form.values());

      // Close form.
      if (closeOnSubmit) {
        this.close();
      }
    } 
    catch (err) {
      this.setSubmitting(false);

      showNotification(errObj2Str(err), NotificationType.error);
      this.handleErrorOnSubmit();
    }
  }

  render() {
    const {
      heading,
      moduleName,
      children,
      loadingData,
      closeComponent,
      formElementProps,
      withHeader,
      withFooter,
      viewOnly
    } = this.props;
    const classes = withStyles.getClasses(this.props);

    // console.log('**** HERE in basic form render');
    // console.log(this.props);
    // console.log(children);

    return (
      <FormContainer classes={{ root: classes.root }}>
        <form className={classes.form} onSubmit={this.handleSubmit.bind(this)} {...formElementProps}>
          {loadingData && <Loading absolute />}
          {withHeader && (
            <FormHeader
              classes={{
                root: classes.header,
                heading: classes.heading,
                closeElement: classes.closeElement,
              }}
              heading={heading}
              moduleName={moduleName}
              closeComponent={closeComponent}
              onClose={this.handleCancel}
            />
          )}
          <FormContent classes={{ root: classes.content }}>
            <div className={cx(classes.children, { [classes.childrenDisabled]: viewOnly })}>{children}</div>
          </FormContent>
          {withFooter && (
            <FormFooter classes={{ root: classes.footer }}>
              <Grid
                container
                alignItems="center"
                wrap="nowrap"
                className={classes.controls}
              >
                {this.controls}
              </Grid>
            </FormFooter>
          )}
        </form>
      </FormContainer>
    );
  }
}

const FormStyled = withStyles(
  Form, 
  (theme, props) => ({
    "root": {},
    "form": {
      width: 'inherit'
    },
    "content": {},
    "header": {},
    "heading": {},
    "closeElement": {},
    "children": {
      maxHeight: 'calc(100vh - 306px)',
      overflow: 'auto',
      padding: theme.spacing(2, 1, 6, 0),
    },
    "childrenDisabled": {
      '& > *': {
        ...theme.shortcuts.disabled
      }
    },
    "createAnotherLabel": {
      fontSize: 13,
      fontWeight: 400,
      color: theme.palette.colors.black,
      whiteSpace: 'nowrap'
    },
    "templateButton": {
      fontSize: 13,
      ...theme.shortcuts.borderPrimarySolid
    },
    "footer": {},
    "controls": {
      '& button': {
        marginRight: theme.spacing(4)
      }
    }
  })
);

export default FormStyled;